import React, { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Link } from "react-router-dom";
import { Mail, Globe, ArrowRight } from "lucide-react";

/* Text is the approved policy from Skillsgain_Privacy_Policy.docx, kept verbatim.
   The two values the source document left blank live here so a policy refresh is
   a one-line edit rather than a hunt through the markup. */
const LAST_UPDATED = "9 October 2026";
const PRIVACY_EMAIL = "info@skillsgain.com";

const SECTIONS = [
  {
    id: "information-we-collect",
    title: "1. Information We Collect",
    intro: "Depending on your interaction with Skillsgain, we may collect:",
    list: [
      "Business contact details such as name, work email, phone number, job title, and organization.",
      "Participant information provided by an enterprise customer for training administration.",
      "Training information such as registration, attendance, assessment, completion, and certification status, where applicable.",
      "Information submitted through website forms, enquiries, event registrations, or business communications.",
      "Technical information such as IP address, browser, device information, cookies, and website usage data.",
    ],
  },
  {
    id: "how-we-use-information",
    title: "2. How We Use Information",
    intro: "We may use personal information to:",
    list: [
      "Respond to business enquiries and requests.",
      "Plan, coordinate, and deliver enterprise training programs.",
      "Manage participant access, attendance, assessments, and completion.",
      "Provide customer and technical support.",
      "Communicate about training programs, schedules, services, and relevant business updates.",
      "Improve our website, learning platforms, and services.",
      "Maintain security, prevent misuse, and meet legal or contractual obligations.",
    ],
  },
  {
    id: "enterprise-customer-participant-information",
    title: "3. Enterprise Customer & Participant Information",
    paragraphs: [
      "An enterprise customer may provide information about its employees or other authorized participants so that Skillsgain can deliver training and provide agreed reports or learning records.",
      "Where Skillsgain processes information on behalf of an enterprise customer, we will handle that information in accordance with the applicable agreement, customer instructions, and applicable law. The enterprise customer is responsible for ensuring it has the appropriate authority or legal basis to provide such information.",
      "Training-related information may be shared with the relevant enterprise customer where this is part of the agreed service.",
    ],
  },
  {
    id: "sharing-of-information",
    title: "4. Sharing of Information",
    intro:
      "Skillsgain may share information where reasonably necessary to provide its services or operate its business, including with:",
    list: [
      "The relevant enterprise customer.",
      "Authorized trainers and program personnel.",
      "Technology, hosting, learning-platform, analytics, security, and other service providers working on our behalf.",
      "Professional advisers or business partners where appropriate.",
      "Government, regulatory, or law-enforcement authorities where required or permitted by law.",
    ],
    callout: "Skillsgain does not sell personal information to third parties.",
  },
  {
    id: "cookies-website-technologies",
    title: "5. Cookies & Website Technologies",
    paragraphs: [
      "Skillsgain may use cookies and similar technologies to operate the website, remember preferences, understand website usage, improve performance, and measure marketing activities. You may manage certain cookies through your browser or available cookie controls. Disabling some cookies may affect website functionality.",
    ],
  },
  {
    id: "data-security",
    title: "6. Data Security",
    paragraphs: [
      "Skillsgain uses reasonable administrative, technical, and organizational measures designed to protect personal information against unauthorized access, disclosure, alteration, loss, or misuse. However, no method of electronic storage or transmission can be guaranteed to be completely secure.",
    ],
  },
  {
    id: "data-retention",
    title: "7. Data Retention",
    paragraphs: [
      "We retain personal information only for as long as reasonably necessary for service delivery, business and contractual requirements, record keeping, dispute resolution, security, and compliance with applicable law. Information processed for an enterprise customer may be retained or deleted according to the applicable customer agreement.",
    ],
  },
  {
    id: "privacy-rights-marketing-preferences",
    title: "8. Privacy Rights & Marketing Preferences",
    paragraphs: [
      "Subject to applicable law, individuals may have rights to request access to, correction of, updating, deletion, or other appropriate handling of their personal information. Where information is processed on behalf of an enterprise customer, Skillsgain may coordinate such requests with that organization.",
      "If you receive promotional communications from Skillsgain, you may opt out using the unsubscribe option in the communication or by contacting us. Service-related communications may still be sent where necessary.",
    ],
  },
  {
    id: "third-party-services",
    title: "9. Third-Party Services",
    paragraphs: [
      "The Skillsgain website or Services may include links to third-party websites, platforms, or services. These third parties have their own privacy practices. Skillsgain is not responsible for the privacy practices of third-party services that it does not control.",
    ],
  },
  {
    id: "updates-to-this-policy",
    title: "10. Updates to This Policy",
    paragraphs: [
      "Skillsgain may revise this Privacy Policy from time to time to reflect changes in our Services, business practices, technology, or applicable requirements. The latest version will be published on our website with the updated “Last Updated” date.",
    ],
  },
];

const PrivacyPolicyPage = () => {
  useEffect(() => {
    const previous = document.title;
    document.title = "Privacy Policy — SkillsGain";
    window.scrollTo(0, 0);
    return () => { document.title = previous; };
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <Header/>
      <PageHero
        eyebrow="LEGAL"
        title="Privacy Policy"
        subtitle="How SkillsGain collects, uses, shares, and protects personal information across our website and enterprise learning services."
      />

      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-[260px_1fr] gap-10">

          {/* Contents rail — sticky on desktop, a plain list above the policy on mobile */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-5">
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">CONTENTS</div>
              <ul className="space-y-2 text-sm">
                {SECTIONS.map(s => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="text-gray-600 hover:text-[#15348C] transition-colors leading-snug block">
                      {s.title}
                    </a>
                  </li>
                ))}
                <li>
                  <a href="#contact-us" className="text-gray-600 hover:text-[#15348C] transition-colors leading-snug block">
                    11. Contact Us
                  </a>
                </li>
              </ul>
            </div>
          </aside>

          <div className="max-w-3xl">
            <Reveal>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#4F9622] bg-[#6BBE32]/10 border border-[#6BBE32]/20 rounded-full px-3 py-1.5">
                Last Updated: {LAST_UPDATED}
              </div>
              <p className="mt-6 text-gray-600 leading-relaxed">
                Skillsgain (“Skillsgain,” “we,” “us,” or “our”) respects your privacy and is committed to protecting
                personal information. This Privacy Policy explains how Skillsgain collects, uses, shares, and protects
                information when you visit our website or use our enterprise training and learning services.
              </p>
              <p className="mt-3 text-gray-600 leading-relaxed">
                Skillsgain primarily provides B2B training services to organizations and their authorized employees,
                contractors, and participants. Where an enterprise customer provides participant information to
                Skillsgain, that information may be processed to deliver the agreed training services and related
                reporting.
              </p>
            </Reveal>

            {SECTIONS.map((s, i) => (
              <Reveal key={s.id} variant="up" delay={(i % 4) * 60}>
                <section id={s.id} className="mt-10 scroll-mt-24">
                  <h2 className="text-[#15348C]">{s.title}</h2>
                  {s.intro && <p className="mt-3 text-gray-600 leading-relaxed">{s.intro}</p>}
                  {s.paragraphs?.map(p => (
                    <p key={p.slice(0, 40)} className="mt-3 text-gray-600 leading-relaxed">{p}</p>
                  ))}
                  {s.list && (
                    <ul className="mt-4 space-y-2.5">
                      {s.list.map(item => (
                        <li key={item} className="flex gap-3 text-gray-600 leading-relaxed">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#6BBE32] shrink-0 mt-2.5"/>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.callout && (
                    <p className="mt-5 border-l-4 border-[#6BBE32] bg-[#F8F9FA] rounded-r-lg px-4 py-3 text-[#15348C] font-semibold">
                      {s.callout}
                    </p>
                  )}
                </section>
              </Reveal>
            ))}

            <Reveal variant="up">
              <section id="contact-us" className="mt-10 scroll-mt-24">
                <h2 className="text-[#15348C]">11. Contact Us</h2>
                <p className="mt-3 text-gray-600 leading-relaxed">
                  For questions, privacy requests, or concerns regarding this Policy, please contact Skillsgain:
                </p>
                <div className="sg-card mt-5 bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6">
                  <div className="font-bold text-[#15348C]">Skillsgain</div>
                  <ul className="mt-3 space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-gray-600">
                      <Globe size={15} className="text-[#6BBE32] shrink-0"/>
                      <a href="https://www.skillsgain.com" className="sg-link hover:text-[#15348C]">www.skillsgain.com</a>
                    </li>
                    <li className="flex items-center gap-2 text-gray-600">
                      <Mail size={15} className="text-[#6BBE32] shrink-0"/>
                      <a href={`mailto:${PRIVACY_EMAIL}`} className="sg-link hover:text-[#15348C]">{PRIVACY_EMAIL}</a>
                    </li>
                  </ul>
                  <Link to="/contact" className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#4F9622] font-semibold sg-link">
                    Contact our team <ArrowRight size={14}/>
                  </Link>
                </div>
              </section>
            </Reveal>
          </div>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default PrivacyPolicyPage;
